import { useEffect, useRef } from "react";
import { gql, useQuery } from "@apollo/client";
import { useIntercom } from "react-use-intercom";
import { isImpersonating, withAuthProvider } from "../../lib/auth";
import { PGA_BACKEND_CLIENT_NAME } from "../../apolloClient";

export const INTERCOM_USER_JWT_QUERY = gql`
  query IntercomUserJwt {
    currentMember {
      intercomUserJwt(platform: WEB)
    }
  }
`;

const resolveSession = ({ impersonating, isLoggedIn, intercomUserJwt }) => {
  if (impersonating) return { kind: "off" };
  if (isLoggedIn && intercomUserJwt) {
    return { kind: "verified", jwt: intercomUserJwt };
  }
  return { kind: "anonymous" };
};

export const Intercom = ({ isLoggedIn }) => {
  const { boot, shutdown, hardShutdown } = useIntercom();
  const impersonating = isImpersonating();
  const { data } = useQuery(INTERCOM_USER_JWT_QUERY, {
    context: { clientName: PGA_BACKEND_CLIENT_NAME },
    skip: !isLoggedIn || impersonating,
  });
  const intercomUserJwt = data?.currentMember?.intercomUserJwt;
  const { kind, jwt } = resolveSession({
    impersonating,
    isLoggedIn,
    intercomUserJwt,
  });
  const appliedSessionRef = useRef(null);

  useEffect(() => {
    const previous = appliedSessionRef.current;
    if (
      previous &&
      previous.kind === kind &&
      (kind !== "verified" || previous.jwt === jwt)
    ) {
      return;
    }

    if (kind === "off") {
      hardShutdown();
    } else {
      if (previous && previous.kind !== "off") {
        shutdown();
      }
      if (kind === "verified") {
        boot({ intercomUserJwt: jwt });
      } else {
        boot();
      }
    }

    appliedSessionRef.current = { kind, jwt };
  }, [kind, jwt, boot, shutdown, hardShutdown]);

  return null;
};

const IntercomWithAuth = withAuthProvider(Intercom);

IntercomWithAuth.displayName = "withAuthProvider(Intercom)";
export default IntercomWithAuth;
